import { useState, useEffect, useRef } from 'react';

const MIN_STATUS_TEXT_DURATION_MS = 1500;

export const useStatusTextDisplay = (loadingStatusText: string[] | null) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const lastRenderAtRef = useRef<number>(0);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset index when array becomes empty
  useEffect(() => {
    if (!loadingStatusText?.length) {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      setCurrentIndex(0);
      lastRenderAtRef.current = 0;
      return;
    }

    // Ensure currentIndex is valid
    if (currentIndex >= loadingStatusText.length) {
      setCurrentIndex(Math.max(0, loadingStatusText.length - 1));
      return;
    }
  }, [loadingStatusText, currentIndex]);

  // Track which index we're timing
  const trackedIndexRef = useRef<number>(-1);

  // Handle timing and progression
  useEffect(() => {
    if (!loadingStatusText?.length) {
      return;
    }

    // Clear any existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    // If we're showing a new index, reset the timer
    const now = Date.now();
    if (trackedIndexRef.current !== currentIndex) {
      trackedIndexRef.current = currentIndex;
      lastRenderAtRef.current = now;
    }

    // Check if we should move to next text
    const hasNext = currentIndex < loadingStatusText.length - 1;

    if (hasNext) {
      const timeSinceStart = now - lastRenderAtRef.current;

      if (timeSinceStart >= MIN_STATUS_TEXT_DURATION_MS) {
        // Move to next text immediately
        setCurrentIndex(currentIndex + 1);
      } else {
        // Wait until minimum duration has passed
        updateTimeoutRef.current = setTimeout(() => {
          setCurrentIndex((prevIndex) => {
            if (prevIndex < loadingStatusText.length - 1) {
              return prevIndex + 1;
            }
            return prevIndex;
          });
          updateTimeoutRef.current = null;
        }, MIN_STATUS_TEXT_DURATION_MS - timeSinceStart);
      }
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, [loadingStatusText, currentIndex]);

  // Return the current status text
  if (!loadingStatusText?.length) {
    return '';
  }

  return loadingStatusText[currentIndex] || '';
};
